import {useEffect, useState} from 'react'
import { hoursMinsSecs } from '../util/time'
import TagView from './TagView'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { showNotification } from '../util/notifications'
import { useMutation, useQuery } from '@apollo/client/react'
import useCurrentUser from '../hooks/useCurrentUser'
import useCurrentRoom from '../hooks/useCurrentRoom'
import useUserIsCurator from '../hooks/useUserIsCurator'
import DeleteTrack from '../graphql/queries/DeleteTrack'
import RemoveTracks from '../graphql/queries/RemoveTrack'
import UpdateTrack from '../graphql/queries/UpdateTrack'
import UpdateRoomTags from '../graphql/queries/UpdateRoomTags'
import Track from '../graphql/queries/Track'
import SearchTracks from '../graphql/queries/SearchTracks'
import { Box, Stack, Group, Text, Button, Paper } from '@mantine/core'
import Spinner from '../ui/Spinner'
import EditableField from '../ui/EditableField'

const cdnUrl = process.env.REACT_APP_CDN_URL

export default function TrackView(props) {

    const dispatch = useAppDispatch()
    const user = useCurrentUser()
    const currentRoom = useCurrentRoom()
    const isCurrentRoomAdmin = useUserIsCurator()
    const [deleteTrackMutation] = useMutation(DeleteTrack, {
        refetchQueries: [ SearchTracks ]
    })
    const [removeTracksMutation] = useMutation(RemoveTracks, {
        refetchQueries: [ SearchTracks ]
    })
    const [updateTrackMutation] = useMutation(UpdateTrack)
    const [updateRoomTagsMutation] = useMutation(UpdateRoomTags)
    const { loading, data } = useQuery(Track, {
        fetchPolicy: 'cache-and-network',
        variables: { id: props.trackId, roomId: currentRoom?.id }
    })

    const [editing, setEditing] = useState(null)
    const [album, setAlbum] = useState('')
    const [artist, setArtist] = useState('')
    const [composer, setComposer] = useState('')
    const [date, setDate] = useState('')
    const [genre, setGenre] = useState('')
    const [title, setTitle] = useState('')
    const [track, setTrack] = useState('')
    const [tags, setTags] = useState([])
    const [globalTags, setGlobalTags] = useState([])
    const [youtubeId, setYoutubeId] = useState('')

    const trackData = data?.track

    const duration = hoursMinsSecs(trackData?.duration ?? 0)

    useEffect(() => {
        resetStateForNewTrack()
    }, [trackData])

    function addTag(tag) {
        if (tags.includes(tag)) return
        const newTags = [...tags, tag].toSorted()
        saveTags(newTags)
    }

    function addGlobalTag(tag) {
        if (globalTags.includes(tag)) return
        const newGlobalTags = [...globalTags, tag].toSorted()
        saveGlobalTags(newGlobalTags)
    }


    function deleteTrack() {
        dispatch(Actions.showConfirmModal(
            'Deleting the track will permanently remove it for everyone FOREVER!  Are you sure you want to proceed?',
            'Delete',
            async () => {
                if (! await deleteTrackMutation({ variables: { trackId: props.trackId } })) {
                    throw Error('Could not delete the track.  Try again later.')
                }
            }
        ))
    }

    function removeFromRoom() {
        dispatch(Actions.showConfirmModal(
            `Are you sure you want to remove ${title} by ${artist}?`,
            'Remove',
            async () => {
                if (!await removeTracksMutation({
                    variables: { roomId: trackData?.roomId, trackIds: [props.trackId] }
                })) {
                    throw Error('Could not remove the track from the room.  Try again later.')
                }
            }
        ))
    }

    function downloadTrack() {
        if (!trackData?.files || trackData.files.length === 0) {
            showNotification({ content: <span className="ErrorMessage">No downloadable files available</span> })
            return
        }

        // Find the highest quality file (prefer hq.m4a over lq.m4a)
        const hq = trackData.files.find(file => file.key.endsWith('hq.m4a'))
        const lq = trackData.files.find(file => file.key.endsWith('lq.m4a'))
        const bestFile = hq || lq

        if (!bestFile) {
            showNotification({ content: <span className="ErrorMessage">No suitable file found for download</span> })
            return
        }

        // Create download URL and trigger download
        const downloadUrl = `${cdnUrl}${bestFile.key}`
        const filename = `${artist} - ${title}.m4a`.replace(/[/\\?%*:|"<>]/g, '-') // sanitize filename

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    function removeTag(tag) {
        const newTags = tags.filter( candidate => candidate !== tag ).toSorted()
        setTags(newTags)
        saveTags(newTags)
    }

    function removeGlobalTag(tag) {
        const newGlobalTags = globalTags.filter( candidate => candidate !== tag ).toSorted()
        setGlobalTags(newGlobalTags)
        saveGlobalTags(newGlobalTags)
    }

    function resetStateForNewTrack() {
        if (props.trackId !== trackData?.id)
            setEditing(null)
        setAlbum(trackData?.album)
        setArtist(trackData?.artist)
        setComposer(trackData?.composer)
        setDate(trackData?.date)
        setGenre(trackData?.genre)
        setTitle(trackData?.title)
        setTrack(trackData?.track)
        setTags(trackData?.tags?.toSorted() ?? [])
        setGlobalTags(trackData?.globalTags?.toSorted() ?? [])
        setYoutubeId(trackData?.youtubeId ?? '')
    }

    async function saveField(field, value) {
        if (trackData[field] !== value) {
            try {
                await updateTrackMutation({
                    variables: {
                        trackId: props.trackId,
                        roomId: currentRoom?.id,
                        updates: { [field]: value }
                    }
                })
            } catch (error) {
                showNotification({ content: <span className="ErrorMessage">Error updating track: { error.message }</span> })
            }
        }
    }

    async function saveTags(newTags) {
        if (!currentRoom?.id) {
            // If not in a room context, show an error
            showNotification({ content: <span className="ErrorMessage">Tags can only be edited within a room context</span> })
            return
        }

        try {
            await updateRoomTagsMutation({
                variables: {
                    roomId: currentRoom.id,
                    trackId: props.trackId,
                    tags: newTags
                }
            })
        } catch (error) {
            showNotification({ content: <span className="ErrorMessage">Error updating tags: { error.message }</span> })
        }
    }

    async function saveGlobalTags(newGlobalTags) {
        try {
            await updateTrackMutation({
                variables: {
                    trackId: props.trackId,
                    roomId: currentRoom?.id,
                    updates: { tags: newGlobalTags }
                }
            })
        } catch (error) {
            showNotification({ content: <span className="ErrorMessage">Error updating global tags: { error.message }</span> })
        }
    }

    function isGlobalAdmin() {
        return user?.roles.includes('admin')
    }

    function userCanEditMetadata() {
        // Global admins can edit all metadata
        if (isGlobalAdmin()) return true
        // Track owners can edit their own tracks (not in a room)
        if (!trackData?.roomId && trackData?.userId === user?.id) return true
        // Room admins cannot edit metadata
        return false
    }

    function userCanEditTags() {
        // Global admins can edit tags
        if (isGlobalAdmin()) return true
        // Track owners can edit tags on their own tracks
        if (!trackData?.roomId && trackData?.userId === user?.id) return true
        // Room admins can edit tags
        return !!isCurrentRoomAdmin
    }

    function shouldShowField(value) {
        // Global admins always see all fields
        if (isGlobalAdmin()) return true
        // Non-admins only see fields that have a value
        return value !== null && value !== undefined && value !== ''
    }

    if (loading) return (
        <Box p="xl" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner />
        </Box>
    )

    return (
        <Box p="md" style={{ position: 'relative' }}>
            {trackData?.coverArt?.thumb500 && [
                <Box
                    style={{
                        position: 'sticky',
                        zIndex: -1,
                        top: 0,
                        width: '100%',
                        maxWidth: '500px',
                        margin: '0 auto',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: '#666',
                    }}
                >
                    <img
                        src={trackData.coverArt?.thumb500}
                        alt={`${trackData.album || 'Track'} cover art`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                        }}
                    />
                </Box>
            ]}

            <div style={{ height: '0.5em', background: 'linear-gradient(to bottom, transparent, #242424)' }} />
            <Stack gap="md">
                {/* Track Metadata Section */}
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(32,32,32, 1)' }}>
                    <Stack gap="sm">
                        {/* Cover Art */}

                        {shouldShowField(title) && (
                            <EditableField label="Title" value={title} onChange={setTitle} fieldName="title" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(artist) && (
                            <EditableField label="Artist" value={artist} onChange={setArtist} fieldName="artist" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(album) && (
                            <EditableField label="Album" value={album} onChange={setAlbum} fieldName="album" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(track) && (
                            <EditableField label="Track" value={track} onChange={setTrack} fieldName="track" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(date) && (
                            <EditableField label="Date" value={date} onChange={setDate} fieldName="date" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(composer) && (
                            <EditableField label="Composer" value={composer} onChange={setComposer} fieldName="composer" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}
                        {shouldShowField(genre) && (
                            <EditableField label="Genre" value={genre} onChange={setGenre} fieldName="genre" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={userCanEditMetadata()} />
                        )}

                        {/* Duration field - always shown */}
                        <Group gap="xs" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                Duration
                            </Text>
                            <Text size="sm">{duration}</Text>
                        </Group>

                        {/* YouTube ID - Only for Global Admins */}
                        {isGlobalAdmin() && (
                            <EditableField label="YouTube ID" value={youtubeId} onChange={setYoutubeId} fieldName="youtubeId" editing={editing} setEditing={setEditing} onSave={saveField} canEdit={true} />
                        )}

                        {/* Tags Section */}
                        <Group gap="xs" align="flex-start" wrap="nowrap">
                            <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                Tags
                            </Text>
                            <Box style={{ flex: 1 }}>
                                <TagView
                                    onClick={() => userCanEditTags() && setEditing('tags')}
                                    onAddTag={addTag}
                                    onRemoveTag={removeTag}
                                    editing={editing === 'tags'}
                                    tags={tags}
                                />
                            </Box>
                        </Group>

                        {/* Global Tags Section - Only for Global Admins */}
                        {isGlobalAdmin() && (
                            <Group gap="xs" align="flex-start" wrap="nowrap">
                                <Text size="sm" fw={500} c="dimmed" style={{ minWidth: '100px', flexShrink: 0 }}>
                                    Global Tags
                                </Text>
                                <Box style={{ flex: 1 }}>
                                    <TagView
                                        onClick={() => setEditing('globalTags')}
                                        onAddTag={addGlobalTag}
                                        onRemoveTag={removeGlobalTag}
                                        editing={editing === 'globalTags'}
                                        tags={globalTags}
                                    />
                                </Box>
                            </Group>
                        )}
                    </Stack>
                </Paper>

                {/* Action Buttons */}
                { isGlobalAdmin() && (
                    <Group justify="flex-end">
                        <Button
                            color="red"
                            variant="light"
                            onClick={deleteTrack}
                            size="sm"
                        >
                            Delete Track
                        </Button>
                        <Button
                            color="blue"
                            variant="light"
                            onClick={downloadTrack}
                            size="sm"
                        >
                            Download Track
                        </Button>
                    </Group>
                )}
            </Stack>
        </Box>
    )
}
